// src/pages/Partnership.tsx
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import WhatsAppFloat from '@/components/WhatsAppFloat'
import Breadcrumb from '@/components/Breadcrumb'
import MetaTags from '@/components/MetaTags'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { useToast } from '@/hooks/use-toast'
import { Handshake, CheckCircle, Loader2 } from 'lucide-react'
import sendStoreContactEmail from '@/services/http/contact/sendEmails'

const partnershipSchema = z.object({
  name: z
    .string()
    .trim()
    .min(3, 'Nome deve ter pelo menos 3 caracteres')
    .max(100, 'Nome muito longo'),
  whatsapp: z
    .string()
    .trim()
    .min(10, 'WhatsApp inválido')
    .max(20, 'WhatsApp muito longo'),
  email: z
    .string()
    .trim()
    .email('Email inválido')
    .max(255, 'Email muito longo'),
  business: z
    .string()
    .trim()
    .min(10, 'Conte-nos mais sobre seu negócio (mínimo 10 caracteres)')
    .max(1000, 'Mensagem muito longa'),
})

type PartnershipFormData = z.infer<typeof partnershipSchema>

const Partnership = () => {
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { toast } = useToast()

  const form = useForm<PartnershipFormData>({
    resolver: zodResolver(partnershipSchema),
    defaultValues: {
      name: '',
      whatsapp: '',
      email: '',
      business: '',
    },
  })

  // Máscara para WhatsApp
  const formatPhone = (phone: string) => {
    const numbers = phone.replace(/\D/g, '')
    const match = numbers.match(/^(\d{0,2})(\d{0,5})(\d{0,4})$/)
    if (!match) return ''
    return !match[2]
      ? match[1]
      : `(${match[1]}) ${match[2]}${match[3] ? `-${match[3]}` : ''}`
  }

  const onSubmit = async (data: PartnershipFormData) => {
    setIsSubmitting(true)

    try {
      await sendStoreContactEmail({
        name: data.name,
        email: data.email,
        phone: data.whatsapp.replace(/\D/g, ''),
        message: data.business,
        form: 'partner_form',
      })

      toast({
        title: 'Solicitação enviada!',
        description:
          'Entraremos em contato em breve para falar sobre a parceria.',
      })

      setIsSubmitted(true)
    } catch (error) {
      console.error('Erro ao enviar solicitação:', error)
      toast({
        title: 'Erro ao enviar',
        description:
          'Ocorreu um erro ao enviar sua solicitação. Tente novamente.',
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col">
      <MetaTags
        title="Seja Parceiro - FotoLivro Store"
        description="Torne-se um parceiro FotoLivro Store. Cadastre-se e descubra como podemos trabalhar juntos."
        type="website"
      />
      <Header />
      <Breadcrumb />
      <main className="flex-1 pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="text-center mb-8">
            <Handshake className="w-16 h-16 mx-auto mb-4 text-primary" />
            <h1 className="text-4xl font-bold mb-4">Quero Ser Parceiro</h1>
          </div>

          <div className="mb-12 text-left space-y-6">
            <h2 className="text-2xl font-serif italic text-gold text-center">
              Vamos construir histórias juntos
            </h2>

            <p className="text-muted-foreground leading-relaxed">
              Na Fotolivro Store, acreditamos que histórias bem contadas merecem
              mais do que espaço digital — elas merecem forma, presença e
              permanência. Transformamos fotografias em álbuns que organizam,
              valorizam e dão continuidade às histórias que realmente importam.
            </p>

            <p className="text-muted-foreground leading-relaxed">
              Por isso, buscamos parcerias com marcas, fotógrafos, estúdios,
              empresas e projetos que compartilham do mesmo olhar: o cuidado com
              o detalhe, o respeito pela trajetória das pessoas e o desejo de
              entregar algo que tenha significado real.
            </p>

            <p className="text-muted-foreground leading-relaxed">
              Ser parceiro da Fotolivro Store é somar forças para oferecer
              experiências completas — onde criação, curadoria e materialização
              caminham juntas. É ampliar valor para o cliente final, fortalecer
              narrativas e criar produtos que atravessam o tempo com propósito.
            </p>

            <p className="text-muted-foreground leading-relaxed">
              Se você acredita que boas histórias merecem ser bem preservadas,
              será um prazer construir essa parceria.
            </p>

            <p className="text-center text-lg font-medium text-foreground">
              Vamos conversar?
            </p>
          </div>

          {!isSubmitted ? (
            <div className="bg-card rounded-lg shadow-lg p-8 border border-border">
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-6"
                  noValidate
                >
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nome Completo *</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Seu nome completo"
                            {...field}
                            autoComplete="name"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="whatsapp"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>WhatsApp *</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="(11) 99999-9999"
                            {...field}
                            value={formatPhone(field.value)}
                            onChange={e => {
                              const formatted = formatPhone(e.target.value)
                              field.onChange(formatted)
                            }}
                            maxLength={15}
                            autoComplete="tel"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email *</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder="seu@email.com"
                            {...field}
                            autoComplete="email"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="business"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Qual é o seu negócio ou atuação profissional? *
                        </FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Conte-nos sobre seu negócio, área de atuação, e como você gostaria de trabalhar conosco..."
                            className="min-h-[120px] resize-y"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="bg-muted/50 p-4 rounded-md">
                    <p className="text-sm text-muted-foreground">
                      * Campos obrigatórios. Após o envio, nossa equipe entrará
                      em contato para discutir as oportunidades de parceria.
                    </p>
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    className="w-full"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      'Enviar Solicitação'
                    )}
                  </Button>
                </form>
              </Form>
            </div>
          ) : (
            <div className="bg-card rounded-lg shadow-lg p-12 border border-border text-center">
              <CheckCircle className="w-20 h-20 mx-auto mb-6 text-green-500" />
              <h2 className="text-3xl font-bold mb-4">Solicitação Recebida!</h2>
              <p className="text-lg text-muted-foreground mb-2">
                Obrigado pelo seu interesse em se tornar nosso parceiro.
              </p>
              <p className="text-lg text-muted-foreground">
                Nossa equipe entrará em contato em breve para falar sobre a
                parceria.
              </p>
              <Button
                onClick={() => (window.location.href = '/')}
                variant="outline"
                size="lg"
                className="mt-8"
              >
                Voltar para a Home
              </Button>
            </div>
          )}
        </div>
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  )
}

export default Partnership
